import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sparkles, AlertTriangle, RefreshCw, X, ChevronRight } from 'lucide-react';
import MealImage from './MealImage';

// PHASE 3 — shared durable AI-log job card. SAME rendering rules on Home and the AI Log screen (no
// per-screen business logic). READY opens the existing durable Review via ?job_id= (never rebuilds the
// draft client-side). Retry/Discard call the existing endpoints via the shared hook. Nothing here ever
// commits/logs a meal.
function StatusChip({ status }) {
  // Restrained, functional color only: analyzing = neutral progress, ready = Fitxess cyan accent
  // (actionable, not a loud success), failed = neutral chip with a small destructive-tinted icon.
  const map = {
    queued: { label: 'Analyzing…', cls: 'text-muted-foreground', iconCls: 'text-muted-foreground', icon: Loader2, spin: true },
    processing: { label: 'Analyzing…', cls: 'text-muted-foreground', iconCls: 'text-muted-foreground', icon: Loader2, spin: true },
    ready: { label: 'Ready to review', cls: 'text-accent', iconCls: 'text-accent', icon: Sparkles },
    failed: { label: "Couldn't analyze", cls: 'text-muted-foreground', iconCls: 'text-destructive/70', icon: AlertTriangle },
  };
  const s = map[status] || map.queued;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${s.cls}`}
      data-testid={`job-status-${status}`}>
      <Icon size={12} className={`${s.iconCls} ${s.spin ? 'animate-spin' : ''}`} />{s.label}
    </span>
  );
}

export function JobStatusCard({ job, onRetry, onDiscard }) {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const title = job.meal_title || (job.input_kind === 'text' ? 'Text meal' : 'Photo meal');
  const summary = job.summary || {};
  const sub = job.status === 'ready' && summary.food_count != null
    ? `${summary.food_count} food${summary.food_count === 1 ? '' : 's'}${summary.calories != null ? ` · ~${summary.calories} cal` : ''}`
    : [job.submitted_local_date, job.suggested_meal_type].filter(Boolean).join(' · ');

  const openReview = () => navigate(`/ai-log/review?job_id=${encodeURIComponent(job.job_id)}`);
  const doRetry = async () => { setBusy(true); try { await onRetry(job.job_id); } finally { setBusy(false); } };
  const doDiscard = async () => { setBusy(true); try { await onDiscard(job.job_id); } finally { setBusy(false); } };

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-border/40 bg-card"
      data-testid={`ai-job-card-${job.job_id}`}>
      <div className="w-11 h-11 rounded-lg overflow-hidden bg-muted/40 shrink-0 flex items-center justify-center">
        {job.image_url
          ? <MealImage src={job.image_url} alt={title} className="w-full h-full object-cover" />
          : <Sparkles size={16} className="text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate">{title}</div>
        <div className="mt-0.5 flex items-center gap-2">
          <StatusChip status={job.status} />
          {sub ? <span className="text-xs text-muted-foreground truncate">{sub}</span> : null}
        </div>
        {job.status === 'failed' && job.error && job.error.message ? (
          <div className="mt-1 text-xs text-muted-foreground truncate" data-testid={`job-error-${job.job_id}`}>
            {String(job.error.message).slice(0, 120)}
          </div>
        ) : null}
      </div>
      <div className="shrink-0 flex items-center gap-2">
        {job.status === 'ready' && (
          <button onClick={openReview} disabled={busy} data-testid={`job-review-btn-${job.job_id}`}
            className="inline-flex items-center gap-1 px-3 h-8 rounded-lg bg-accent text-accent-foreground text-xs font-semibold hover:opacity-90 transition-opacity">
            Review <ChevronRight size={14} />
          </button>
        )}
        {job.status === 'failed' && (
          <>
            <button onClick={doRetry} disabled={busy} data-testid={`job-retry-btn-${job.job_id}`}
              className="inline-flex items-center gap-1 px-3 h-8 rounded-lg bg-accent text-accent-foreground text-xs font-semibold hover:opacity-90 transition-opacity">
              <RefreshCw size={13} className={busy ? 'animate-spin' : ''} /> Retry
            </button>
            <button onClick={doDiscard} disabled={busy} data-testid={`job-discard-btn-${job.job_id}`}
              aria-label="Dismiss" className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted/50 transition-colors">
              <X size={15} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// READY notification modal. Review opens durable Review; X ONLY dismisses (no discard/commit/nav/status
// change). The caller tracks dismissed job_ids so a dismissed READY job does not reopen every poll tick.
export function ReadyMealModal({ job, onClose }) {
  const navigate = useNavigate();
  if (!job) return null;
  const summary = job.summary || {};
  const line = summary.food_count != null
    ? `${summary.food_count} food${summary.food_count === 1 ? '' : 's'}${summary.calories != null ? ` · ~${summary.calories} cal` : ''}`
    : (job.meal_title || 'Your meal');
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="ready-meal-modal">
      <div className="w-full max-w-sm rounded-2xl bg-card border border-border/50 p-5 shadow-2xl">
        <div className="flex items-start justify-between">
          <div className="inline-flex items-center gap-2 text-accent">
            <Sparkles size={18} /><span className="text-sm font-semibold">Meal ready to review</span>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="ready-modal-close"
            className="p-1 rounded-lg text-muted-foreground hover:bg-muted/50 transition-colors"><X size={18} /></button>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{line}</p>
        <button onClick={() => navigate(`/ai-log/review?job_id=${encodeURIComponent(job.job_id)}`)}
          data-testid="ready-modal-review-btn"
          className="mt-4 w-full h-10 rounded-xl bg-accent text-accent-foreground text-sm font-semibold hover:opacity-90 transition-opacity">
          Review
        </button>
      </div>
    </div>
  );
}

export default JobStatusCard;
